import ErrorBoundary from 'components/error-boundaries';
import { SectionTitle } from 'modules/marketplace-performance-page/components/section-title';
import { OpenPositionsStats } from 'api/types/httpsTypes/marketplace-performance';
import Table, { Body } from 'modules/shared-components/data-display/table';
import {
  openPositionsColumn,
  Position,
} from 'modules/marketplace-performance-page/components/open-positions-chart-column';
import {
  ColumnsData,
  tradesDistributionColumn,
} from 'modules/marketplace-performance-page/components/trades-distribution-chart-column';
import PieChart from 'modules/marketplace-performance-page/components/pie-chart';
import MainListComponent from 'modules/marketplace-performance-page/components/main-list-open-positions-component';
import Tooltip from 'modules/shared-components/tooltip';
import { FullPageNotification } from 'modules/bots-performance/components/full-page-notification';
import Big from 'big.js';

interface Props {
  openPositionsStats: OpenPositionsStats;
}

const OpenPositions = ({ openPositionsStats }: Props) => {
  const mainOpenPositionsStats: {
    title: string;
    value: number;
    suffix: string | null;
    tooltip: string;
  }[] = [
    {
      title: 'Unrealized PnL',
      value: openPositionsStats?.overview?.unrealisedPnL * 100 || 0,
      suffix: '%',
      tooltip: 'The current PnL of the open positions.',
    },
    {
      title: 'Open Positions',
      value: openPositionsStats?.overview?.numberPositions || 0,
      suffix: null,
      tooltip: 'The current number of opened positions.',
    },
    {
      title: 'Hanging Positions',
      value: openPositionsStats?.overview?.hangingPositions || 0,
      suffix: null,
      tooltip:
        'The number of open positions that are open for more than 30 days.',
    },
  ];

  const openPositionDistributionTableData: ColumnsData = openPositionsStats
    ?.tradesDistribution?.chart
    ? openPositionsStats?.tradesDistribution?.chart
        ?.slice(0, 4)
        .map((openPositionDistribution) => {
          return openPositionDistribution;
        })
    : [];

  if (openPositionDistributionTableData.length === 4) {
    openPositionDistributionTableData.push({
      fraction: openPositionsStats?.tradesDistribution?.others?.fraction || '0',
      holdingTime:
        openPositionsStats?.tradesDistribution?.others?.holdingTime || '0',
      avgReturns:
        openPositionsStats?.tradesDistribution?.others?.avgReturns || '0',
    });
  }

  if (openPositionsStats?.overview?.numberPositions === 0) {
    return (
      <ErrorBoundary>
        <div className="flex justify-center items-center w-full">
          <FullPageNotification
            height="60vh"
            title="There is no open position to show here"
            subTitle="This bot does not have any open positions to show. Come back in the next few days!"
          />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="flex space-x-3 ">
          {mainOpenPositionsStats.map(
            (mainOpenPositionStats, index: number) => (
              <MainListComponent
                key={index}
                data={mainOpenPositionStats}
                index={index}
              />
            )
          )}
        </div>

        <SectionTitle>
          Trades Distribution{' '}
          <Tooltip text="An overview about the current open positions distribution, including pairs, relative number of hits, distribution over trading volume, holding time and avg. PnL over capital deployed.">
            <span className="d-inline-block">
              <i className="mdi mdi-information-outline" />
            </span>
          </Tooltip>
        </SectionTitle>
        <div className="lg:flex">
          <div className=" lg:w-1/3 flex justify-center items-center xxs:my-2 lg:my-0">
            <PieChart
              suffix="%"
              labels={
                openPositionsStats?.tradesDistribution?.chart.map((prop) => {
                  return prop.base + '/' + prop.quote;
                }) || []
              }
              series={
                openPositionsStats?.tradesDistribution?.chart.map((prop) => {
                  return Big(prop.fraction).mul(100).toNumber();
                }) || []
              }
            />
          </div>
          <div className=" lg:w-2/3  flex justify-center items-center">
            <Table>
              <Table.Header columns={tradesDistributionColumn} />
              <Body
                columns={tradesDistributionColumn}
                data={[...(openPositionDistributionTableData || [])].map(
                  (distribution, index) => ({
                    ...distribution,
                    id: `${index}`,
                  })
                )}
              />
            </Table>
          </div>
        </div>

        <SectionTitle>
          Last 10 Positions{' '}
          <Tooltip text="The last 10 positions opened by the bot. The data is not real-time, a random updating delay is applied.">
            <span className="d-inline-block">
              <i className="mdi mdi-information-outline" />
            </span>
          </Tooltip>
        </SectionTitle>
        <Table>
          <Table.Header columns={openPositionsColumn} />
          <Body
            columns={openPositionsColumn}
            data={
              [...(openPositionsStats?.positions || [])]
                .reverse()
                .map((position, index) => ({
                  ...position,
                  id: `${index}`,
                })) as Position[]
            }
          />
        </Table>
      </div>
    </ErrorBoundary>
  );
};
export default OpenPositions;
